import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PublicPage, Seo } from "@/components/public/PublicChrome";
import { LeadCapture } from "@/components/public/LeadCapture";
import { TOOLS, PROD_ORIGIN } from "@/pages/tools/toolsConfig";
import { useEffect } from "react";

function JsonLd({ data }) {
  useEffect(() => {
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.text = JSON.stringify(data);
    document.head.appendChild(el);
    return () => { document.head.removeChild(el); };
  }, [data]);
  return null;
}

export default function PublicToolsIndex() {
  const tools = Object.values(TOOLS);
  const breadcrumb = {
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${PROD_ORIGIN}/` },
      { "@type": "ListItem", position: 2, name: "Free Business Tools", item: `${PROD_ORIGIN}/tools` },
    ],
  };
  return (
    <PublicPage>
      <Seo title="Free Business Tools" path="/tools"
        description="Free AI-powered business tools: a business name generator, idea validator, business plan generator, and an LLC vs S-corp structure recommender. No signup required to start." />
      <JsonLd data={breadcrumb} />
      <section className="max-w-5xl mx-auto px-6 pt-32 pb-20">
        <div className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase mb-3">Free Business Tools</div>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-4" data-testid="tools-index-h1">
          Free tools to launch your business
        </h1>
        <p className="text-base sm:text-lg text-[#A1A1AA] max-w-2xl mb-10">
          Get real, useful results in seconds — no signup required to start. Create a free account any time to save your work and keep going.
        </p>
        <div className="grid gap-4 sm:grid-cols-2" data-testid="tools-index-grid">
          {tools.map((t) => (
            <Link key={t.slug} to={`/tools/${t.slug}`} data-testid={`tools-index-${t.slug}`}
              className="group rounded-2xl border border-white/10 bg-[#121214] p-6 hover:border-[#D4AF37]/40 transition-colors">
              <h2 className="font-heading text-2xl text-white mb-2 group-hover:text-[#D4AF37] transition-colors">{t.h1}</h2>
              <p className="text-sm text-[#A1A1AA] mb-4">{t.metaDescription}</p>
              <span className="inline-flex items-center text-sm text-[#D4AF37]">Try it free <ArrowRight className="w-4 h-4 ml-1.5" /></span>
            </Link>
          ))}
        </div>
        <div className="mt-10 max-w-2xl">
          <LeadCapture />
        </div>
      </section>
    </PublicPage>
  );
}
